import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { vqeCopy } from '../../ux/vqeCopy';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';

// A simple (optionally qualified) identifier → muted `qualifier.` prefix +
// emphasised name. Anything else (`*`, aggregates, arithmetic, function calls)
// returns prefix=null and renders as a single segment (decision D5).
const SIMPLE_QUALIFIED = /^([\w"`$#[\]]+)\.([\w"`$#*[\]]+)$/;

export function splitColumnLabel(expression: string): { prefix: string | null; name: string } {
  const expr = expression.trim();
  const q = SIMPLE_QUALIFIED.exec(expr);
  if (q) return { prefix: `${q[1]}.`, name: q[2] };
  return { prefix: null, name: expr };
}

export function ColumnLabel({
  expression,
  outOfScope,
  testId,
}: {
  readonly expression: string;
  readonly outOfScope: boolean;
  readonly testId: string;
}) {
  const { prefix, name } = splitColumnLabel(expression);
  return (
    <span className="truncate font-mono text-xs" data-testid={testId}>
      {prefix ? (
        <span className={outOfScope ? 'text-red-600/80 dark:text-red-400/80' : 'text-muted-foreground'}>
          {prefix}
        </span>
      ) : null}
      <span className={`font-medium ${outOfScope ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
        {name}
      </span>
    </span>
  );
}

export interface SelectColumnChipProps {
  readonly id: string;
  readonly index: number;
  readonly content: ReactNode;
  readonly alias?: string | null;
  readonly outOfScope: boolean;
  readonly sortable: boolean;
  readonly removable: boolean;
  readonly onRemove?: () => void;
  readonly extra?: ReactNode;
  readonly hasSubquery?: boolean;
  readonly hasWindow?: boolean;
}

export function SelectColumnChip({
  id,
  index,
  content,
  alias,
  outOfScope,
  sortable,
  removable,
  onRemove,
  extra,
  hasSubquery = false,
  hasWindow = false,
}: SelectColumnChipProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !sortable,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  } as const;

  const tone = outOfScope ? 'border-red-500/50 bg-red-500/10' : 'border-border bg-muted/30';
  const grab = sortable ? 'cursor-grab active:cursor-grabbing touch-none ' : '';
  // ANA-02/03 layout artifact: a window-function chip must always break onto
  // its own row in the shared flex-wrap SELECT list (never float to the
  // right of plain-column chips, which is pure wrap-timing). basis-full +
  // w-full on a flex-wrap child forces it to consume the full row width, so
  // it deterministically starts a new line — in both full and compact mode.
  const fullRow = hasWindow ? 'w-full basis-full ' : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`vqe-select-list-item-${index}`}
      data-out-of-scope={outOfScope ? 'true' : 'false'}
      data-has-subquery={hasSubquery ? 'true' : 'false'}
      data-has-window={hasWindow ? 'true' : 'false'}
      className={`group inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-xs ${fullRow}${grab}${tone}`}
      {...(sortable ? attributes : {})}
      {...(sortable ? listeners : {})}
    >
      {content}
      {outOfScope ? (
        <VqeTooltip
          selfProvider
          content="This column references a table that is not in FROM/JOIN. Add the table or remove the column."
        >
          <span
            className="shrink-0 cursor-help rounded bg-red-500/15 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400"
            data-testid={`vqe-select-list-out-of-scope-${index}`}
          >
            unknown table
          </span>
        </VqeTooltip>
      ) : null}
      {alias ? (
        <span
          // `min-w-0 truncate`: the alias must ellipsize INSIDE the chip
          // rather than push itself (and the remove ×) past the chip border.
          className="min-w-0 truncate rounded bg-background px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
          data-testid={`vqe-select-list-alias-${index}`}
        >
          {alias}
        </span>
      ) : null}
      {extra}
      {/* Remove button is ALWAYS rendered to preserve the VQE-RO scaffolding
          contract (read-only shows every affordance, disabled). Edit mode:
          enabled + fades in on hover. Read-only: disabled + statically visible. */}
      <span
        className={
          removable
            ? 'shrink-0 opacity-40 transition-opacity group-hover:opacity-100'
            : 'shrink-0'
        }
        onPointerDown={(e) => e.stopPropagation()}
      >
        <RemoveButton
          testId={`vqe-select-list-remove-${index}`}
          ariaLabel="Remove column"
          disabled={!removable}
          tooltip={vqeCopy.disabled.removeColumnNotReady}
          onClick={removable ? onRemove : undefined}
        />
      </span>
    </div>
  );
}
